import React, { useEffect, useState } from 'react';
import { DragDropContext, Droppable, DropResult } from 'react-beautiful-dnd';
import styled from 'styled-components';

import { IconButton } from '@material-ui/core';
import { AddCircleOutline } from '@material-ui/icons';
import { ConfigHeader } from '@sierralabs/forms-ui';
import ConfigCard from '@sierralabs/forms-ui/dist/config/components/ConfigCard';
import { Shape, StateEvent, StateNode } from '@sierralabs/workflow';

import { calculateReorderIndex } from '../../../common/calculateReorderIndex';
import { reorder } from '../../../common/reorder.helper';
import StateEventEditor, { StateEventEditorProps } from './StateEventEditor';

const Styled = {
  Container: styled.div<{ isDragging?: boolean }>`
    width: 100%;
    ${props => (props.isDragging ? 'padding-bottom: 48px' : '')};
  `
};

export interface StateEventEditorContainerProps {
  shape: Shape;
  editorProps: Omit<StateEventEditorProps, 'shape' | 'event' | 'index'>;
  onChange?: () => void;
}

const StateEventEditorContainer: React.FC<StateEventEditorContainerProps> = (
  props: StateEventEditorContainerProps
) => {
  const { shape, editorProps, onChange } = props;
  const { events = [] } = shape.data || {};
  const [expanded, setExpanded] = useState<number>(-1);
  const [isDragging, setIsDragging] = useState<boolean>(false);

  const moveItem = (sourceIndex: number, destinationIndex: number) => {
    // move expanded item
    setExpanded(calculateReorderIndex(expanded, sourceIndex, destinationIndex));
    const updatedItems = reorder(events, sourceIndex, destinationIndex);
    if (shape.data) {
      shape.data.events = updatedItems;
      editorProps.updateShape();
      editorProps.updateWorkflow();
    }
  };

  const handleDragEnd = (result: DropResult) => {
    setIsDragging(false);
    const { source, destination } = result;
    // dropped outside the list
    if (!destination || source.droppableId !== destination.droppableId) {
      return;
    }
    moveItem(source.index, destination.index);
  };

  // reset expanded when shape is updated
  useEffect(() => setExpanded(-1), [shape]);

  return (
    <ConfigCard>
      <ConfigHeader
        end={
          <IconButton
            size='small'
            onClick={() => {
              const stateNode = shape.data as StateNode;
              stateNode.addEvent({
                transitions: [
                  {
                    target: stateNode.path
                  }
                ]
              });
              onChange?.();
            }}
          >
            <AddCircleOutline />
          </IconButton>
        }
      >
        Transition
      </ConfigHeader>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId='events'>
          {(provided, _snapshot) => (
            <Styled.Container ref={provided.innerRef} {...{ isDragging }}>
              {events.map((event: StateEvent, index: number) => (
                <StateEventEditor
                  {...editorProps}
                  {...{ shape, event, index, setIsDragging }}
                  key={event.id}
                  expanded={index === expanded}
                  setExpanded={(expanded: boolean) => {
                    setExpanded(expanded ? index : -1);
                  }}
                />
              ))}
            </Styled.Container>
          )}
        </Droppable>
      </DragDropContext>
    </ConfigCard>
  );
};

export default StateEventEditorContainer;
